/**
 * «С нами с» и «Бронирует за» в «Инфо о клиенте» (27.10.2026, даты брони из Квестгида). Сервер считает сводку
 * (apps/api/src/utils/clientOrigin.ts), здесь — только подписи. Даты сервера — UTC; показываем день по Оренбургу (UTC+5).
 * В карточке (ClientInfoCard, блок сводки) — originRows: две строки «С нами с» и «Бронирует», только когда данные есть.
 */

export interface ClientOriginView {
  /** UTC 'ГГГГ-ММ-ДД чч:мм:сс'. */
  first_booked_at: string | null;
  first_src: "kvestgid" | "v4" | null;
  /** 'ГГГГ-ММ-ДД' — дата игры, уже местная. */
  first_game_date: string | null;
  lead_days_median: number | null;
  lead_n: number;
  kg_count: number;
  kg_cancelled: number;
}

const OREN_MS = 5 * 3_600_000;
const SRC: Record<string, string> = { kvestgid: "Квестгид" };

/** Русское число: plural(5, "бронь", "брони", "броней") → «броней». */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return many;
  return b === 1 ? one : b >= 2 && b <= 4 ? few : many;
}

/** 'ГГГГ-ММ-ДД' → 'ДД.ММ.ГГГГ'. */
export const dateRu = (d: string): string => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;

/** UTC 'ГГГГ-ММ-ДД чч:мм:сс' → день по Оренбургу 'ГГГГ-ММ-ДД'; не дата — "". */
export function orenDay(utc: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/.exec(utc);
  const ms = m ? Date.parse(`${m[1]}T${m[2]}Z`) : NaN;
  return Number.isFinite(ms) ? new Date(ms + OREN_MS).toISOString().slice(0, 10) : "";
}

/** UTC 'ГГГГ-ММ-ДД чч:мм:сс' → день по Оренбургу 'ДД.ММ.ГГГГ'; не дата — "". */
export function orenDateRu(utc: string): string {
  const d = orenDay(utc);
  return d ? dateRu(d) : "";
}

/** Значение строки «С нами с» — более ранняя из двух дат: день первой известной брони («12.03.2018 · Квестгид») или первой игры
 *  («12.03.2018 · первая игра»). Ревью 27.10: у игр 1С без даты Квестгида и у мартовского импорта дата брони неизвестна или позже
 *  игры (created_at — момент переноса), и «С нами с» по одной дате брони был бы позже игры в истории ниже. Ничего — null. */
export function sinceValue(o: ClientOriginView | null | undefined): string | null {
  if (!o) return null;
  const booked = o.first_booked_at ? orenDay(o.first_booked_at) : "";
  const game = o.first_game_date && /^\d{4}-\d{2}-\d{2}$/.test(o.first_game_date) ? o.first_game_date : "";
  if (booked && (!game || booked <= game)) return SRC[o.first_src ?? ""] ? `${dateRu(booked)} · ${SRC[o.first_src ?? ""]}` : dateRu(booked);
  return game ? `${dateRu(game)} · первая игра` : null;
}

/** Значение строки «Бронирует за»: «~7 дн.»; меньше суток — «менее чем за сутки» (не «в день игры»: вечером накануне —
 *  тоже меньше суток); нет данных — null. */
export function leadValue(o: ClientOriginView | null | undefined): string | null {
  if (!o || o.lead_days_median === null || o.lead_n < 1) return null;
  return o.lead_days_median < 1 ? "менее чем за сутки" : `~${Math.round(o.lead_days_median)} дн.`;
}

/** Подсказка к сроку: «по 12 броням», «по 21 брони». */
export function leadHint(o: ClientOriginView | null | undefined): string {
  if (!o || o.lead_n < 1) return "";
  const n = o.lead_n;
  return `по ${n} ${n % 10 === 1 && n % 100 !== 11 ? "брони" : "броням"}`;
}

/** «12 броней в Квестгиде, 3 отмены»; ни одной — null. */
export function kgValue(o: ClientOriginView | null | undefined): string | null {
  if (!o || o.kg_count < 1) return null;
  const base = `${o.kg_count} ${plural(o.kg_count, "бронь", "брони", "броней")} в Квестгиде`;
  return o.kg_cancelled > 0 ? `${base}, ${o.kg_cancelled} ${plural(o.kg_cancelled, "отмена", "отмены", "отмен")}` : base;
}

/** Готовые строки для карточки: «С нами с: …», «Бронирует за …». */
export function originLines(o: ClientOriginView | null | undefined): string[] {
  const out: string[] = [];
  const since = sinceValue(o), lead = leadValue(o);
  if (since) out.push(`С нами с: ${since}`);
  if (lead) out.push(lead.startsWith("~") ? `Бронирует за ${lead}` : `Бронирует ${lead}`);
  return out;
}

/** Строки сводки карточки (ключ — значение, как остальные строки): «С нами с — 12.03.2018 · Квестгид», «Бронирует — за ~7 дн.»
 *  (подсказка — по скольким броням). Данных нет — строки нет. */
export function originRows(o: ClientOriginView | null | undefined): { k: string; v: string; hint?: string }[] {
  const out: { k: string; v: string; hint?: string }[] = [];
  const since = sinceValue(o), lead = leadValue(o);
  if (since) out.push({ k: "С нами с", v: since });
  if (lead) out.push({ k: "Бронирует", v: lead.startsWith("~") ? `за ${lead}` : lead, hint: leadHint(o) });
  return out;
}
